import React, { useCallback, useEffect, useState } from "react";
import * as api from "./csaApi";

/**
 * One question, visit by visit (design §10c).
 *
 * **Not a line chart.** A question's answer is Yes, Partial, No or N/A — four
 * named states, not a quantity. Drawing them as a line invites reading the
 * slope between two points as a rate of improvement, which is meaningless for
 * a single question and actively wrong across a version boundary. So: one chip
 * per visit, read left to right.
 *
 * The break is the reason this screen exists. Where the admin said a reworded
 * question is no longer the same question, the row is cut and the cut is
 * labelled. A client looking at "No → Yes" must be able to see that the
 * question changed underneath it, because otherwise the chart claims an
 * improvement nobody earned.
 *
 * Each visit shows the wording *as it was at that visit*, not as it is now.
 * Labelling an old point with today's text would hide exactly the change the
 * break is announcing.
 */

const ANSWER = {
  yes: { label: "Yes", cls: "yes" },
  partial: { label: "Partial", cls: "partial" },
  no: { label: "No", cls: "no" },
  na: { label: "N/A", cls: "na" },
};

export default function CsaQuestionTrend({ orgId, siteId }) {
  const [questions, setQuestions] = useState(null);
  const [code, setCode] = useState("");
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* The picker resets whenever the site does — a code from another site's
     history would simply come back empty and look like a bug. */
  useEffect(() => {
    let dead = false;
    setQuestions(null);
    setCode("");
    setSeries(null);
    if (!orgId || !siteId) return undefined;

    api
      .trendQuestions(orgId, siteId)
      .then((r) => { if (!dead) setQuestions(r.questions || []); })
      .catch((e) => { if (!dead) setError(e.message || "Could not load the questions"); });

    return () => { dead = true; };
  }, [orgId, siteId]);

  const load = useCallback(
    async (c) => {
      if (!c) { setSeries(null); return; }
      setLoading(true);
      setError(null);
      try {
        setSeries(await api.questionTrend(orgId, siteId, c));
      } catch (e) {
        setError(e.message || "Could not load that question");
      } finally {
        setLoading(false);
      }
    },
    [orgId, siteId]
  );

  if (error) return <div className="qt-error">{error}</div>;
  if (!questions) return null;
  if (!questions.length) {
    return (
      <div className="qt">
        <h3>One question over time</h3>
        <p className="qt-blurb">
          Nothing to compare yet — this site has one visit, or none that reached a
          finished audit.
        </p>
      </div>
    );
  }

  const repeated = questions.filter((q) => q.visits > 1).length;

  return (
    <div className="qt">
      <div className="qt-head">
        <div>
          <h3>One question over time</h3>
          <p className="qt-blurb">
            {repeated
              ? `${repeated} of ${questions.length} questions have been answered more than once here.`
              : "Every question here has been answered once, so there is nothing to compare yet."}
          </p>
        </div>
        <select
          aria-label="Question"
          value={code}
          onChange={(e) => { setCode(e.target.value); load(e.target.value); }}
        >
          <option value="">Pick a question…</option>
          {questions.map((q) => (
            <option key={q.code} value={q.code}>
              {q.code} · {q.question.length > 70 ? q.question.slice(0, 70) + "…" : q.question}
              {q.visits > 1 ? ` (${q.visits} visits)` : ""}
            </option>
          ))}
        </select>
      </div>

      {loading ? <p className="qt-blurb">Loading…</p> : null}

      {series && series.points.length ? (
        <>
          <Verdict summary={series.summary} />
          <ol className="qt-track">
            {series.points.map((p, i) => (
              <React.Fragment key={p.audit_id}>
                {p.break_before ? <Break reason={p.break_reason} /> : null}
                <Visit point={p} prev={i > 0 && !p.break_before ? series.points[i - 1] : null} />
              </React.Fragment>
            ))}
          </ol>
        </>
      ) : null}

      {series && !series.points.length ? (
        <p className="qt-blurb">That question has not been answered at this site.</p>
      ) : null}
    </div>
  );
}

/**
 * The one-line reading of the series — and deliberately silent where it cannot
 * honestly say anything. The server withholds `direction` across a break; this
 * says why rather than leaving a blank space that looks like a bug.
 */
function Verdict({ summary }) {
  if (!summary) return null;

  if (summary.breaks) {
    return (
      <p className="qt-verdict broken">
        Not comparable across {summary.breaks === 1 ? "one version change" : `${summary.breaks} version changes`}.
        The question was revised, so these visits are not measuring the same thing
        and no trend is claimed.
      </p>
    );
  }

  if (summary.visits < 2) {
    return <p className="qt-verdict">One visit — nothing to compare yet.</p>;
  }

  const word =
    summary.direction === "improved" ? "Improved"
      : summary.direction === "declined" ? "Declined"
        : summary.direction === "unchanged" ? "Unchanged"
          : null;

  if (!word) {
    return (
      <p className="qt-verdict">
        {summary.visits} visits. Not applicable at one of them, so no direction is stated.
      </p>
    );
  }

  return (
    <p className={"qt-verdict " + summary.direction}>
      {word} across {summary.visits} visits — {label(summary.first_answer)} to{" "}
      {label(summary.last_answer)}.
    </p>
  );
}

function Break({ reason }) {
  return (
    <li className="qt-break" role="separator">
      <span className="qt-break-rule" aria-hidden="true" />
      <span className="qt-break-text">{reason}</span>
    </li>
  );
}

function Visit({ point, prev }) {
  const a = ANSWER[point.answer] || { label: point.answer || "—", cls: "na" };
  const moved = prev && prev.answer !== point.answer;

  return (
    <li className="qt-visit">
      <div className="qt-when">{fmtDate(point.start_date)}</div>
      <div className={"qt-chip " + a.cls}>{a.label}</div>
      {moved ? (
        <div className="qt-moved">
          from {label(prev.answer)}
        </div>
      ) : null}
      {/* The wording at this visit. Where it differs from the visit before, the
          difference is the story. */}
      <p className="qt-text">{point.question}</p>
      {point.comment ? <p className="qt-comment">{point.comment}</p> : null}
      <div className="qt-ver">{point.framework_version}</div>
    </li>
  );
}

const label = (a) => (ANSWER[a] ? ANSWER[a].label : a || "—");

function fmtDate(d) {
  const s = String(d || "").slice(0, 10);
  if (!s) return "—";
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day)} ${months[Number(m) - 1] || m} ${y}`;
}
